import React, { useMemo } from "react";
import SlidePanel from "../add_customer/SlidePanel";
import { ArrowLeft, X } from "lucide-react";

const ReviewGuarantorship = ({
  isReviewGuarantor,
  handleCloseReviewGuarantor,
  handleBackToSearch,
  selectedCandidate,
  handleAddGuarantor,
  isSubmitting,
  guaranteedAmount,
  setGuaranteedAmount,
}) => {
  const availableCapacity = Number(selectedCandidate?.availableCapacity ?? 0);

  const numericAmount = Number(guaranteedAmount || 0);

  const amountError = useMemo(() => {
    if (!guaranteedAmount) return "";

    if (numericAmount <= 0) {
      return "Amount must be greater than 0. Please enter a valid guaranteed amount.";
    }

    if (numericAmount > availableCapacity) {
      return "The entered amount exceeds this member's available guaranteeing limit. Please enter a lower amount.";
    }

    return "";
  }, [guaranteedAmount, numericAmount, availableCapacity]);

  const isValid =
    guaranteedAmount && numericAmount > 0 && numericAmount <= availableCapacity;

  return (
    <SlidePanel isOpen={isReviewGuarantor} onClose={handleCloseReviewGuarantor}>
      <div className="relative flex flex-col h-full bg-white rounded-[12px]">
        {/* Back */}
        <button onClick={handleBackToSearch} className="absolute left-3 top-2">
          <ArrowLeft size={25} />
        </button>

        {/* Close */}
        <button
          onClick={handleCloseReviewGuarantor}
          className="absolute right-3 top-2"
        >
          <X size={25} />
        </button>

        <div className="flex flex-col h-full px-5 pt-16">
          <h2 className="font-[700] text-[20px] text-[#000000]">
            Review & Allocate Guarantorship
          </h2>

          <p className="text-[#4A4747] text-[14px] font-[300]">
            Verify the member's identity and available capacity, then specify
            their guaranteed allocation below.
          </p>

          <h3 className="mt-5 font-[600] text-[16px] text-[#4A4747]">
            Selected Member Details
          </h3>

          <div className="grid grid-cols-2 gap-y-5 mt-4">
            <div>
              <p className="font-[500] text-[16px] text-[#000000]">Member ID</p>
              <p className="text-[#316CE9] text-[15px] font-[400]">
                #{selectedCandidate?.public_id}
              </p>
            </div>

            <div>
              <p className="font-[500] text-[16px] text-[#000000]">
                First name
              </p>
              <p className="text-[#4F4F4F] text-[15px] font-[400]">
                {selectedCandidate?.firstname}
              </p>
            </div>

            <div>
              <p className="font-[500] text-[16px] text-[#000000]">
                Middle name
              </p>
              <p className="text-[#4F4F4F] text-[15px] font-[400]">
                {selectedCandidate?.middlename}
              </p>
            </div>

            <div>
              <p className="font-[500] text-[16px] text-[#000000]">Last name</p>
              <p className="text-[#4F4F4F] text-[15px] font-[400]">
                {selectedCandidate?.lastname}
              </p>
            </div>

            <div>
              <p className="font-[500] text-[16px] text-[#000000]">
                Phone number
              </p>
              <p className="text-[#4F4F4F] text-[15px] font-[400]">
                {selectedCandidate?.mobileno}
              </p>
            </div>

            <div>
              <p className="font-[500] text-[16px] text-[#000000]">Email</p>
              <p className="text-[#4F4F4F] text-[15px] font-[400]">
                {selectedCandidate?.email}
              </p>
            </div>

            <div>
              <p className="font-[500] text-[16px] text-[#000000]">
                Available capacity
              </p>

              <p className="text-[#4F4F4F] text-[15px] font-[400]">
                KES{" "}
                {Number(availableCapacity || "0.00")?.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
          </div>

          <div className="mt-10">
            <label className="font-medium text-[16px]">Guaranteed Amount</label>

            <div
              className={`flex mt-3 rounded-lg overflow-hidden border ${
                amountError ? "border-[#FF5C5C]" : "border-[#E5E5E5]"
              }`}
            >
              <div className="w-[90px] bg-[#D6D6D6] flex justify-center items-center">
                KES
              </div>

              <input
                value={guaranteedAmount}
                onChange={(e) => setGuaranteedAmount(e?.target?.value)}
                className="flex-1 h-[52px] bg-[#EAEAEA] px-4 outline-none"
                placeholder="0.00"
                type="number"
              />
            </div>

            {amountError && (
              <p className="text-[#FF4D4F] text-[13px] mt-2">{amountError}</p>
            )}
          </div>

          <div className="flex-1" />

          <button
            onClick={handleAddGuarantor}
            disabled={!isValid || isSubmitting}
            className={`mb-8 h-[54px] rounded-lg text-white font-medium ${
              isValid && !isSubmitting ? "bg-[#0B4D86]" : "bg-[#D9D9D9]"
            }`}
          >
            {isSubmitting ? "Adding Guarantor..." : "Add Guarantor"}
          </button>
        </div>
      </div>
    </SlidePanel>
  );
};

export default ReviewGuarantorship;
